import React, { useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { isValidHexColor } from "helper/briqueTypeAccent";

const MAIN_SCROLL_CONTAINER_ID = "main";

/** ID d'ancre aligné sur BriquesSection (scroll vers la carte). */
export function briqueScrollAnchorId(devisId) {
  return `brique-anchor-${devisId}`;
}

export function scrollToBriqueAnchor(devisId) {
  const el = document.getElementById(briqueScrollAnchorId(devisId));
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function navLabel(brique) {
  const raw = typeof brique.emoji === "string" ? brique.emoji.trim() : "";
  if (raw) return { kind: "emoji", value: raw };
  const n = (brique.name || "?").trim();
  return { kind: "text", value: n.slice(0, 2).toUpperCase() };
}

/**
 * Menu flottant : raccourcis vers chaque brique (emoji ou initiales).
 * Position fixe en bas à droite, aligné sur la colonne #main (Global.js).
 * Affiché seulement s'il y a au moins 2 briques.
 */
const BriqueFloatingNav = ({ briquesDevis = [] }) => {
  const [fixedBox, setFixedBox] = useState({
    right: 0,
    bottom: 24,
  });

  const updateFixedPosition = useCallback(() => {
    const main = document.getElementById(MAIN_SCROLL_CONTAINER_ID);
    if (!main) return;
    const rect = main.getBoundingClientRect();
    const inset = 0;
    const bottom = 24;
    setFixedBox({
      right: Math.max(inset, window.innerWidth - rect.right + inset),
      bottom,
    });
  }, []);

  useLayoutEffect(() => {
    if (briquesDevis.length < 2) return;

    updateFixedPosition();

    window.addEventListener("resize", updateFixedPosition);
    window.addEventListener("scroll", updateFixedPosition, true);

    const main = document.getElementById(MAIN_SCROLL_CONTAINER_ID);
    let ro;
    if (main && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateFixedPosition());
      ro.observe(main);
    }

    return () => {
      window.removeEventListener("resize", updateFixedPosition);
      window.removeEventListener("scroll", updateFixedPosition, true);
      if (ro) ro.disconnect();
    };
  }, [briquesDevis.length, updateFixedPosition]);

  if (briquesDevis.length < 2) return null;

  const nav = (
    <nav
      className="brique-floating-nav py-2 px-2"
      aria-label="Aller à une brique du devis"
      style={{
        position: "fixed",
        borderRadius: "10px 0 O 10px",
        right: fixedBox.right,
        bottom: fixedBox.bottom,
        zIndex: 1020,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        gap: "8px",
        alignItems: "center",
        maxWidth: "min(calc(100vw - 24px), 560px)",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        backgroundColor: "#689D71",
        border: "1px solid rgba(255, 255, 255, 0.35)",
        boxShadow:
          "0 2px 8px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.08)",
        pointerEvents: "auto",
      }}
    >
      {briquesDevis.map((b) => {
        const label = navLabel(b);
        const accent = b.accent_color || b.accentColor;
        const accentRing = isValidHexColor(accent)
          ? { boxShadow: `0 0 0 2px ${accent.trim()}` }
          : {};
        return (
          <button
            key={b._devisId}
            type="button"
            className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 40,
              height: 40,
              fontSize: label.kind === "emoji" ? "1.15rem" : "0.65rem",
              fontWeight: 700,
              lineHeight: 1,
              ...accentRing,
            }}
            title={b.name || "Brique"}
            aria-label={`Aller à la brique ${b.name || ""}`}
            onClick={() => scrollToBriqueAnchor(b._devisId)}
          >
            {label.value}
          </button>
        );
      })}
    </nav>
  );

  return createPortal(nav, document.body);
};

export default BriqueFloatingNav;
